import SimpleMonacoEditor from "@/components/CodeEditors/SimpleMonacoEditor";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAiContextStore } from "@/store/zustand/aiContextStore";
import useLayoutStore, { LeftTab } from "@/store/zustand/layoutStore";
import { Message } from "@/store/zustand/supabaseStore";
import { getEditorLanguageForFilename, useCanvasId } from "@/utils/utils";
import {
  CheckIcon,
  ChevronDown,
  ChevronRight,
  ChevronUpIcon,
  CircleAlertIcon,
  Code,
  EyeIcon,
  HistoryIcon,
  PlayIcon,
  XIcon,
} from "lucide-react";
import { FilepathLink } from "../FilepathLink";
import React, { useEffect, useMemo, useState } from "react";
import { ChatAiCodeChanges } from "../ChatAiCodeChanges";
import { useApplyChatBlock } from "../useApplyChatBlock";
import { MessageSegment } from "../utils/messages";
import { useDispatch, useSelector } from "react-redux";
import useActivityStore from "@/store/zustand/activityStore";
import { EngSpecFileDiff } from "../../EngSpec/EngSpec";
import { useSession } from "@clerk/clerk-react";

export interface CommitProps {
  message: Message;

  block: MessageSegment;
  blockIndex: number;

  loadingAiChange: boolean;
}

export const EngSpecBlock = React.memo(
  ({ message, block, blockIndex, loadingAiChange }: CommitProps) => {
    const [contextExpanded, setContextExpanded] = useState(loadingAiChange);

    // Keep expanded while streaming, collapse when done
    useEffect(() => {
      if (loadingAiChange) {
        setContextExpanded(true);
      } else {
        setContextExpanded(false);
      }
    }, [loadingAiChange]);

    const dispatch = useDispatch();

    const activityStreamList = useActivityStore(
      (state) => state.activityStreamList,
    );

    const setEngSpecOpenedFile = useAiContextStore(
      (state) => state.setEngSpecOpenedFile,
    );

    const appliedBlock = message.message_context?.applied_segment_indices?.find(
      (idx: number) => idx === blockIndex,
    );

    const restoreActivity = useActivityStore((state) => state.restoreActivity);

    const triggerRefetchActivityStream = useActivityStore(
      (state) => state.triggerRefetchActivityStream,
    );

    const { savedActivity, savedActivityIsTerminal, savedActivityIsCurrent } =
      useMemo(() => {
        const savedActivity =
          message.message_context?.savedActivityId === "RAN_COMMAND" ||
          activityStreamList?.find?.(
            (activity) =>
              message.message_context?.savedActivityId === activity.id,
          );
        const savedActivityIsTerminal =
          message.message_context?.savedActivityId === "RAN_COMMAND";

        const currentActivity = activityStreamList?.find?.(
          (activity) => !activity.change_undone,
        );

        const savedActivityIsCurrent =
          !savedActivityIsTerminal && currentActivity === savedActivity;

        return {
          savedActivity,
          savedActivityIsTerminal,
          savedActivityIsCurrent,
        };
      }, [message.message_context?.savedActivityId, activityStreamList]);

    const canvasId = useCanvasId();

    const aiChatEngSpecMessageId = useAiContextStore(
      (state) => state.aiChatEngSpecMessageId,
    );
    const aiChatEngSpecBlockIndex = useAiContextStore(
      (state) => state.aiChatEngSpecBlockIndex,
    );

    const leftTab = useLayoutStore((state) => state.leftTab);
    const setNewLayout = useLayoutStore((state) => state.setNewLayout);

    const openAiChatEngSpec = useAiContextStore(
      (state) => state.openAiChatEngSpec,
    );
    const closeAiChatEngSpec = useAiContextStore(
      (state) => state.closeAiChatEngSpec,
    );

    const { applyChatBlock, canApplyChatBlock } = useApplyChatBlock();

    const { session } = useSession();

    const showOpenButton =
      message.role !== "user" && block.type === "aiChanges";

    const aiChangesOpened =
      showOpenButton &&
      aiChatEngSpecMessageId === message.id &&
      aiChatEngSpecBlockIndex === blockIndex &&
      leftTab === LeftTab.EngSpec;

    const showViewCloseButton =
      message.role === "user" &&
      (message.message_context?.commit ||
        message.message_context?.file ||
        message.message_context?.plan ||
        message.message_context?.codeSnippet) &&
      !message.message_context?.makeNew;

    const canApplyBlock = useMemo(() => {
      // If it was applied before means it was allowed
      if (appliedBlock) return true;

      return canApplyChatBlock(message, block);
    }, [appliedBlock, message, block, canApplyChatBlock]);

    const showApplyButton = message.role !== "user" && canApplyBlock; //canApplySegments[index];

    let codeSnippetFilename, codeSnippetStartLine, codeSnippetEndLine;
    if (block.type === "aiCodeChange" || block.type === "codeSnippet") {
      codeSnippetFilename =
        block.type === "aiCodeChange"
          ? block.aiCodeChange?.filename
          : block.codeSnippet?.filename;
      codeSnippetStartLine =
        block.type === "aiCodeChange"
          ? block.aiCodeChange.edit?.startLine
          : block.codeSnippet?.startLine;
      codeSnippetEndLine =
        block.type === "aiCodeChange"
          ? block.aiCodeChange.edit?.endLine
          : block.codeSnippet?.endLine;
      if (codeSnippetFilename?.includes?.(canvasId)) {
        codeSnippetFilename = codeSnippetFilename.split(canvasId + "/")[1];
      }
    }

    const restoreDisabled =
      savedActivityIsTerminal || savedActivityIsCurrent || !savedActivity;
    let icon, text;
    if (savedActivityIsTerminal) {
      text = "Terminal";
    } else if (savedActivityIsCurrent) {
      icon = <HistoryIcon className="w-3.5 h-3.5" />;
      text = "Current";
    } else if (!savedActivity) {
      icon = <CircleAlertIcon className="w-3.5 h-3.5" />;
      text = "Overwritten";
    } else {
      icon;
      text = "Restore";
    }

    const handleOpenAiChatEngSpec = async (file?: EngSpecFileDiff) => {
      if (showOpenButton) {
        handleShowOpenButtonState(file);
        return;
      }

      if (showViewCloseButton) {
        setContextExpanded(!contextExpanded);
        return;
      }

      if (appliedBlock) {
        return;
      }

      applyChatBlock(message, block, blockIndex);
    };

    const handleShowOpenButtonState = (file?: EngSpecFileDiff) => {
      if (!aiChangesOpened) {
        // @ts-expect-error TS2345
        setNewLayout(LeftTab.EngSpec, null);
      }

      if (aiChangesOpened && !file) {
        closeAiChatEngSpec();
      } else {
        openAiChatEngSpec(blockIndex, message.id);

        if (file) {
          setEngSpecOpenedFile(file.id, Boolean(file?.termCommand));
        }
      }
    };

    return (
      <div
        className={cn(
          "flex flex-col grow bg-secondary-n-950 rounded-md border border-[#2F3035] mt-2 mb-2",
        )}
      >
        <div className="w-full flex items-center gap-1 px-1">
          <div className="flex gap-1 items-center flex-1">
            <div
              className="flex text-xs items-center gap-1.5 cursor-pointer"
              onClick={() => setContextExpanded(!contextExpanded)}
            >
              {contextExpanded ? (
                <ChevronDown className="h-4 w-4 text-secondary-n-400" />
              ) : (
                <ChevronRight className="h-4 w-4 text-secondary-n-400" />
              )}
              Code Changes
            </div>

            {block.type === "aiCodeChange" ||
              (block.type === "codeSnippet" && (
                <div
                  className={
                    "bg-secondary-500 text-secondary-800 rounded-md text-2xs px-1.5 h-5 flex items-center grow-0 w-auto"
                  }
                >
                  {`Code: ${codeSnippetFilename}:${codeSnippetStartLine}-${codeSnippetEndLine}`}
                </div>
              ))}
          </div>

          <div className="flex items-center shrink-0">
            <div
              data-tooltip-id="tooltip"
              data-tooltip-content={
                savedActivityIsCurrent ? "This change is already current" : text
              }
              data-tooltip-place="top"
            >
              <Button
                size="xs"
                variant="ghost"
                disabled={restoreDisabled}
                title={text}
                className={cn(
                  "text-2xs h-6 px-1.5 gap-1 rounded-md hover:bg-secondary-n-900",
                  loadingAiChange && "invisible",
                  !message.message_context?.savedActivityId && "invisible",
                  savedActivityIsTerminal && "text-secondary-n-50",
                )}
                onClick={() => {
                  if (!session || !savedActivity || savedActivity === true)
                    return;

                  setNewLayout(LeftTab.History, undefined);
                  restoreActivity(
                    dispatch,
                    canvasId,
                    savedActivity.id,
                    activityStreamList,
                    session,
                  );
                }}
              >
                {icon || <HistoryIcon className="w-3.5 h-3.5" />}
              </Button>
            </div>
            {showOpenButton && aiChangesOpened ? (
              <Button
                size="xs"
                variant="ghost"
                title="Close"
                className={cn(
                  "text-2xs h-6 px-1.5 gap-1 rounded-md hover:bg-secondary-n-900",
                  block.jsonIsBroken && "hidden",
                )}
                onClick={() => handleOpenAiChatEngSpec()}
              >
                <XIcon className="w-3.5 h-3.5" />
              </Button>
            ) : showOpenButton && !aiChangesOpened ? (
              <FilepathLink
                onClick={() => handleOpenAiChatEngSpec()}
                ariaLabel="Open in editor"
                tooltipContent="Open in editor"
              />
            ) : (
              <Button
                size="xs"
                variant="ghost"
                title={
                  showViewCloseButton
                    ? contextExpanded
                      ? "Close"
                      : "View"
                    : appliedBlock
                      ? "Applied"
                      : "Apply"
                }
                className={cn(
                  "text-2xs h-6 px-1.5 gap-1 rounded-md hover:bg-secondary-n-900",
                  !showViewCloseButton &&
                    !showApplyButton &&
                    !showOpenButton &&
                    "hidden",
                  appliedBlock &&
                    !showViewCloseButton &&
                    !showOpenButton &&
                    "cursor-default",
                  block.jsonIsBroken && "hidden",
                )}
                onClick={() => handleOpenAiChatEngSpec()}
              >
                {showViewCloseButton ? (
                  contextExpanded ? (
                    <ChevronUpIcon className="w-3.5 h-3.5" />
                  ) : (
                    <EyeIcon className="w-3.5 h-3.5" />
                  )
                ) : appliedBlock ? (
                  <CheckIcon className="w-3.5 h-3.5" />
                ) : (
                  <PlayIcon className="w-3.5 h-3.5" />
                )}
              </Button>
            )}
          </div>
        </div>
        <div
          className={cn(
            !contextExpanded && "hidden",
            "border-t border-[#2F3035] -mx-1 px-1 pt-1 mt-1 pb-0",
          )}
        >
          {block.jsonIsBroken ? (
            <div className="text-xs p-1.5">
              Change provided by the AI is not valid JSON. Please try again.
            </div>
          ) : block.type === "aiChanges" ? (
            <ChatAiCodeChanges
              openAiChatEngSpec={(file) => {
                handleOpenAiChatEngSpec(file);
              }}
              changes={block.aiChanges?.changes}
              loadingAiChange={loadingAiChange}
              messageContext={message.message_context}
            />
          ) : block.type === "codeSnippet" || block.type === "aiCodeChange" ? (
            <SimpleMonacoEditor
              theme={"tempo-theme"}
              value={
                block.type === "aiCodeChange"
                  ? block.aiCodeChange?.edit?.newContent?.join?.("\n")
                  : block.codeSnippet?.contents
              }
              additionalOptions={{
                readOnly: true,
                lineNumbers: (num) =>
                  (
                    (block.type === "aiCodeChange"
                      ? block.aiCodeChange?.edit?.startLine || 0
                      : block.codeSnippet.startLine) +
                    num -
                    1
                  ).toString(),
              }}
              language={getEditorLanguageForFilename(
                block.type === "aiCodeChange"
                  ? block.aiCodeChange?.filename
                  : block.codeSnippet.filename,
              )}
              originalValue={
                block.type === "codeSnippet"
                  ? block?.diffCodeSnippet?.contents
                  : undefined
              }
              diff={
                block?.diffCodeSnippet?.contents ||
                block?.diffCodeSnippet?.contents === ""
              }
            />
          ) : null}
        </div>
      </div>
    );
  },
);
